import React, { useContext, useMemo, useState } from "react";
import { PartidasContratoContext } from "../context/PartidasContratoContext";
import { EstimacionesContext } from "../context/EstimacionesContext";
import ConceptoDetail from "../components/conceptos/ConceptoDetail";
import { ConceptosContext } from "../context/ConceptosContext";
import { PreciarioContext } from "../context/PreciarioContext";
import { ModalContext } from "../context/ModalContext";
import useProyecto from "./useProyecto";
import { CapacitorContext } from "../context/CapacitorContext";
import { ContratosContext } from "../context/ContratosContext";
import { useParams } from "react-router-dom";
import BitacoraConcepto from "../components/conceptos/BitacoraConceptos";
import { EstimacionVersionesContext } from "../context/EstimacionVersionesContext";
import ConceptoPrecioAutorizadoForm from "../components/conceptos/ConceptoPrecioAutorizadoForm";
import { formatMonto } from "../utils";
import ConceptoEstimacionDetail from "../components/conceptos/ConceptoEstimacionDetail";
import {
  getAnticipo,
  getTotalPagos,
  getTotalPagosAmortizados,
  getTotalPagosCuenta,
} from "../utils/pagos";
import { PagosContext } from "../context/PagosContext";
import { MontosContext } from "../context/MontosContext";

const usePreciario = () => {
  const [query, setQuery] = useState("");
  const [showConceptos, setShowConceptos] = useState([]);

  const params = useParams();

  const {
    concepto,
    conceptos,
    conceptosExtra,
    setConcepto,
    getConceptosEstimacionVersion,
  } = useContext(ConceptosContext);
  const { estimacionversion } = useContext(EstimacionVersionesContext);
  const { estimacion, acumulados } = useContext(EstimacionesContext);
  const { view, setView, editMode } = useContext(PreciarioContext);
  const { clearModal, modalComponent } = useContext(ModalContext);
  const { partidas } = useContext(PartidasContratoContext);
  const { contrato } = useContext(ContratosContext);
  const { platform } = useContext(CapacitorContext);
  const { montos } = useContext(MontosContext);
  const { pagos } = useContext(PagosContext);
  const { idProyecto, permiso } = useProyecto();

  const idContrato = contrato?.idContrato;
  const idEstimacion = estimacion?.idEstimacion;
  const idEstimacionVersion = params.idEstimacionVersion
    ? params.idEstimacionVersion
    : estimacionversion?.idEstimacionVersion;


  const totalAmortizado = useMemo(
    () => getTotalPagosAmortizados(pagos),
    [pagos]
  );
  const totalCuenta = useMemo(() => getTotalPagosCuenta(pagos), [pagos]);
  const totalPagos = useMemo(() => getTotalPagos(pagos), [pagos]);
  const anticipo = useMemo(() => getAnticipo(pagos), [pagos]);

  const porcentajeAnticipo = useMemo(
    () => parseFloat((anticipo / montos?.autorizado || 1) * 100).toFixed(2),
    [anticipo, montos]
  );

  const porcentajeCuenta = useMemo(
    () =>
      parseFloat(
        ((anticipo + totalCuenta) / montos?.autorizado || 1) * 100
      ).toFixed(2),
    [anticipo, totalCuenta, montos]
  );

  const porAmortizar = useMemo(
    () => anticipo + totalCuenta - totalAmortizado,
    [anticipo, totalCuenta, totalAmortizado]
  );

  function handleComments(concepto) {
    modalComponent(
      "Comentarios",
      <BitacoraConcepto
        idFolder={null}
        idConcepto={concepto.idConcepto}
        idEstimacionVersion={idEstimacionVersion}
      />
    );
  }

  function handleEditPrecio(concepto) {
    const handleCallback = () => {
      clearModal();
      // Reload the estimacion version conceptos so the new authorized price
      // (precio_control) is reflected in the UI.
      if (idEstimacionVersion) {
        getConceptosEstimacionVersion(idProyecto, idEstimacionVersion);
      }
    };
    modalComponent(
      "Autorizar Precio",
      <ConceptoPrecioAutorizadoForm
        handleCallback={handleCallback}
        handleCancel={clearModal}
        concepto={concepto}
      />
    );
  }

  function showConceptoEstimacionDetail(concepto) {
    setConcepto(concepto);
    if (platform !== "web") {
      modalComponent(
        concepto.clave,
        <ConceptoEstimacionDetail />
      );
    }
  }

  function showConceptoDetail(concepto) {
    setConcepto(concepto);
    if (platform !== "web") {
      modalComponent(`Concepto ${concepto.clave}`, <ConceptoDetail />);
    }
  }

  function toggleConceptosPartida(idPartidaContrato) {
    const current = [...showConceptos];
    const index = showConceptos.indexOf(idPartidaContrato);
    if (index === -1) current.push(idPartidaContrato);
    else current.splice(index, 1);
    setShowConceptos(current);
  }

  let partidasRender = Array.isArray(partidas)
    ? [...partidas].sort((a, b) => (parseFloat(a.clave) || 0) - (parseFloat(b.clave) || 0))
    : [];
  let conceptosRender = Array.isArray(conceptos) ? [...conceptos] : [];

  if (editMode === "partidas") {
    partidasRender = partidas.filter(
      (partida) => String(partida.idPartidaContrato)[0] !== "d"
    );
  }

  if (editMode === "conceptos-extra") {
    conceptosRender = conceptosExtra.filter(
      (concepto) => String(concepto.idConceptoExtra)[0] !== "d"
    );
  }

  if (editMode === "conceptos-estimacion") {
    if (["finanzas", "obras"].includes(permiso)) {
      let conceptosEstimados = conceptos.filter(
        (concepto) => concepto.estimado
      );
      let partidasConceptosEstimados = conceptosEstimados.map(
        (concepto) => concepto.idPartidaContrato
      );
      partidasRender = partidasRender.filter((partida) =>
        partidasConceptosEstimados.includes(partida.idPartidaContrato)
      );
    }
  }

  if (estimacion !== null) {
    partidasRender = partidasRender.filter((partida) => {
      return (
        conceptos.find(
          (concepto) => concepto.idPartidaContrato === partida.idPartidaContrato
        ) !== undefined
      );
    });
  }

  function getConceptosPartida(idPartidaContrato) {
    return conceptosRender.filter(
      (concepto) => concepto.idPartidaContrato === idPartidaContrato
    );
  }

  function renderPrecioControl(concepto) {
    if (
      permiso === "finanzas" &&
      ["conciliada-con-obras", "conciliada-con-obras-sin-contratista"].includes(
        estimacionversion?.estado
      )
    ) {
      return (
        <div className="row mx-0">
          <div className="col-12 col-md-9 px-0">
            ${formatMonto(concepto.precio_control || concepto.precio_unitario)}
          </div>
          <div className="col-12 col-md-3 px-0">
            <button
              onClick={() => handleEditPrecio(concepto)}
              className="btn btn-sm btn-outline-primary"
            >
              <i className="fa fa-edit" />
            </button>
          </div>
        </div>
      );
    }
    const precio_control = concepto.precio_control || concepto.precio;
    return <span>${formatMonto(precio_control)}</span>;
  }

  return {
    // Montos
    ...(montos || {}),
    anticipo,
    totalPagos,
    totalCuenta,
    porAmortizar,
    totalAmortizado,
    porcentajeCuenta,
    porcentajeAnticipo,

    pagos: pagos || [],

    view,
    query,
    setView,
    setQuery,
    concepto,
    editMode,
    contrato,
    acumulados,
    idContrato,
    estimacion,
    setConcepto,
    idEstimacion,
    showConceptos,
    conceptosExtra,
    handleComments,
    handleEditPrecio,
    estimacionversion,
    showConceptoDetail,
    getConceptosPartida,
    idEstimacionVersion,
    renderPrecioControl,
    toggleConceptosPartida,
    partidas: partidasRender,
    conceptos: conceptosRender,
    showConceptoEstimacionDetail,
  };
};

export default usePreciario;
